<script setup>
import { computed, ref } from 'vue';
import IModal from '@components/IModal/IModal.vue';
import useCartStore from '@vueApp/store/modules/cart.js';

const cartStore = useCartStore();

const props = defineProps({
  additionalServices: {
    type: Array,
    default() {
      return [];
    },
  },
  additionalTickets: {
    type: Array,
    default() {
      return [];
    },
  },
  orderId: {
    type: Number,
    required: true,
  },
});

const message = computed(() => (props.additionalTickets.length > 0 ? 'Услуга добавлена' : 'Добавить услугу'));
const icon = computed(() => (props.additionalTickets.length > 0 ? 'service-added' : 'service-add'));

function isParkingService(serviceId) {
  return serviceId === 37 || serviceId === 38;
}

const isShowModal = ref(false);
const selectedService = ref(null);
const selectedServiceData = computed(() => ({
  orderId: props.orderId,
  parent: selectedService.value?.parent,
  serviceId: selectedService.value?.service_id,
  priceTypeId: selectedService.value?.view_id,
}));
const isScheme = ref(false);
const serviceId = ref(0);

function showModal() {
  if (props.additionalTickets.length <= 0) isShowModal.value = true;
}

function saveService() {
  const additionalServicesPayload = {
    items: [selectedServiceData.value],
  };
  const jsonPayload = JSON.stringify(additionalServicesPayload);
  cartStore.setExtraService(jsonPayload);
  isShowModal.value = false;
}

function showScheme(id) {
  isScheme.value = !isScheme.value;
  serviceId.value = id;
}

const schemeSrc = computed(() => {
  switch (serviceId.value) {
    case 38:
      return '/images/parking-scheme/parking-vip.jpg';
    case 37:
      return '/images/parking-scheme/parking.jpg';
    default:
      return '';
  }
});
</script>

<template>
  <button class="service-added" @click="showModal">
    <div class="service-added__icon">
      <svg width="17" height="17" aria-hidden="true" focusable="false">
        <use :xlink:href="`#${icon}`" />
      </svg>
    </div>
    <div class="service-added__message">{{ message }}</div>
  </button>
  <i-modal
    v-model="isShowModal"
    class="service-added-modal"
    :is-wide="isScheme"
  >
    <template #header>
      <div v-if="isScheme">
        <button class="service-added__back" @click="isScheme = false">
          <svg width="16" height="16" aria-hidden="true" focusable="false">
            <use xlink:href="#additional-service-back" />
          </svg>
          Назад
        </button>
      </div>
      <div v-else>
        <h1 class="service-added-modal__title">Дополнительные услуги</h1>
        <p>Вы можете выбрать дополнительную услугу из списка. </p>
        <p>
          Вернуть денежные средства за дополнительные услуги можно только при полном возврате билета.
          Частично отказаться от услуг нельзя.
        </p>
      </div>
    </template>

    <template #body>
      <div v-if="isScheme" class="service-item__scheme-container">
        <img class="service-item__scheme-img" :src="schemeSrc" alt="">
      </div>
      <template v-else>
        <div
          v-for="serviceItem in additionalServices"
          :key="serviceItem.service_id + '-' + serviceItem.view_id"
          class="service-item__container"
        >
          <label class="radio-custom">
            <input
              v-model="selectedService"
              :value="serviceItem"
              type="radio"
              class="radio-custom__input"
              :name="'additional-service-' + serviceItem.service_id + '-' + serviceItem.view_id"
            >
            <span class="radio-custom__icon" />
            <span class="radio-custom__block" />
          </label>
          <div class="service-item__main-info">
            <div class="service-item__name"> {{ serviceItem?.name }} </div>
            <div class="service-item__desc"> {{ serviceItem?.view_name }} </div>
            <div
              v-if="isParkingService(serviceItem?.service_id)"
              class="service-item__scheme-link"
              @click="showScheme(serviceItem?.service_id)"
            >
              Посмотреть схему
            </div>
          </div>
          <div class="service-item__price"> {{ serviceItem?.price }} <span class="ruble">₽</span></div>
          <div class="service-item__img-container">
            <p class="service-item__link-back" />
            <img class="service-item__img" src="" alt="">
          </div>
        </div>

        <button
          class="btn btn__primary service-added-modal__button"
          @click="saveService"
        >
          Добавить
        </button>
      </template>
    </template>
  </i-modal>
</template>

<style lang="scss">
.service-added {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: $green;
  margin-top: 9px;

  &__message {
    display: flex;
    align-items: center;
    font-size: 12px;
  }

  &__icon {
    margin-right: 8px;
  }

  &__back {
    color: $primary;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  @media (max-width: $screen-xs) {
    &__back {
      position: fixed;
      bottom: 60px;
      left: 50%;
      transform: translate(-50%);
      z-index: 1001;
    }
  }
}

.service-added-modal {
  &__title {
    font-size: 30px;
    font-weight: 700;
  }

  &__subtitle {
    color: $black;
    font-size: 14px;
  }

  &__button {
    width: 100%;
    margin-top: 24px;
  }

  .btn-close {
    position: absolute;
    color: $primary;
    top: 16px;
    right: 16px;
    opacity: 1;
  }

  .modal-header {
    padding: 48px 48px 0 48px;
    margin-bottom: 16px;
  }

  .modal-body {
    padding: 0 48px 48px;
  }

  @media (max-width: $screen-xs) {
    .modal-dialog {
      margin: 0;
      height: 100%;
    }

    .modal-content {
      border-radius: 0;
      height: 100%;
      overflow-y: auto;
    }

    .modal-header {
      padding: 24px 16px 0;
    }

    .modal-body {
      padding: 0 16px 32px;
      flex: 0;
      flex-grow: 1;
      display: grid;
    }

    &__title {
      font-size: 20px;
    }
  }
}

.service-item {
  &__container {
    display: flex;
    align-items: flex-start;
    padding: 12.5px 0;
  }

  &__main-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex-grow: 1;
  }

  &__name {
    color: $black;
    font-size: 18px;
    font-weight: 700;
  }

  &__desc {
    font-size: 14px;
  }

  &__price {
    font-size: 18px;
    font-weight: 700;
    color: $black;
  }

  &__scheme-link {
    font-size: 14px;
    color: $small-text;
    cursor: pointer;

    &:hover {
      color: $color;
    }
  }

  &__scheme-img {
    display: flex;
    margin-left: auto;
    margin-right: auto;
  }

  @media (max-width: $screen-xs) {
    &__name {
      font-size: 16px;
    }

    &__scheme-container {
      display: flex;
      height: 100%;
      justify-content: center;
      align-items: center;
    }
  }
}
</style>
